import { zeroAddress } from 'viem'
import { Lender } from '@1delta/lender-registry'
import { parseRawAmount } from '../../../utils/parsing'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMultiAccountTypeUserState } from '../utils'
import {
  LenderCrossPoolMeta,
  LenderToLenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import { FLUID_USER_CALL_COUNT } from './userCallBuild'
import { getCachedFluidDexState } from '../../public-data/fluid/dexState'

/**
 * One underlying leg of a vault side, resolved against the public-data meta.
 *
 * `perShare` is set only on a SMART side, where the vault's raw amounts are
 * 1e18 DEX LP shares rather than tokens.
 */
interface PositionLeg {
  token: string
  marketUid: string
  decimals: number
  displayPrice: number
  oraclePrice: number
  priceHist: number
  perShare?: bigint
}

const FLUID_EEE_LOWER = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee'
const normalizeUnderlying = (addr: string): string =>
  addr === FLUID_EEE_LOWER ? zeroAddress : addr

function toBigInt(v: any): bigint {
  if (v === undefined || v === null || v === '0x') return 0n
  if (typeof v === 'bigint') return v
  try {
    return BigInt(v as any)
  } catch {
    return 0n
  }
}

/**
 * Returns a [converter, expectedCallCount] tuple for Fluid user data.
 *
 * Single call `positionsByUser(account)` returns two parallel arrays:
 *   data[0] = (UserPosition[], VaultEntireData[])
 *
 * Each NFT is an independent position with its own health factor, so we
 * model them as sub-accounts (same pattern as Init Capital). The nftId
 * becomes the sub-account identifier.
 *
 * Output is keyed by `FLUID_<vaultId>` (multi-market). Within each vault,
 * each NFT is a separate sub-account with two lending positions (collateral
 * + loan side), its own health factor, and its own liquidation risk.
 */
export const getFluidUserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  meta?: LenderToLenderCrossPoolMeta,
): [(data: any[]) => { [lender: string]: UserData } | undefined, number] => {
  return [
    (data: any[]) => {
      if (!data || data.length !== FLUID_USER_CALL_COUNT) return undefined

      const result = data[0]
      if (!result) return undefined
      const positions = result[0] as any[] | undefined
      const vaults = result[1] as any[] | undefined
      if (
        !Array.isArray(positions) ||
        !Array.isArray(vaults) ||
        positions.length === 0
      )
        return undefined

      // Share -> token conversion for SMART (T2/T3/T4) sides. Primed by the
      // public-data fetch, which must have run for `meta` to exist at all —
      // this parser skips any vault with no market row. If it is somehow cold,
      // smart vaults are omitted rather than reported as share counts.
      const dexState = getCachedFluidDexState(chainId)

      // Group NFTs by vault. Each vault gets its own UserData with
      // sub-accounts per NFT (mirrors Init's per-posId pattern).
      const perVault = new Map<number, Array<{ pos: any; vault: any }>>()

      for (let i = 0; i < positions.length; i++) {
        const pos = positions[i]
        const vault = vaults[i]
        if (!vault?.constantVariables) continue
        if (pos.isLiquidated) continue

        const vaultId = Number(vault.constantVariables.vaultId ?? 0)
        if (!perVault.has(vaultId)) perVault.set(vaultId, [])
        perVault.get(vaultId)!.push({ pos, vault })
      }

      if (perVault.size === 0) return undefined

      const out: { [lender: string]: UserData } = {}

      for (const [vaultId, nfts] of perVault) {
        const lenderKey = `${Lender.FLUID}_${chainId}_${vaultId}`
        const metaMap: LenderCrossPoolMeta | undefined = meta?.[lenderKey]
        if (!metaMap) continue

        // All NFTs in a vault share the same sides.
        const vaultData = nfts[0].vault
        const constants = vaultData.constantVariables
        const isSmartCol = !!vaultData.isSmartCol
        const isSmartDebt = !!vaultData.isSmartDebt

        const sideLegs = (
          side: any,
          isSmart: boolean,
          sideContract: any,
          perShareOf: (s: any) => [bigint, bigint],
        ): PositionLeg[] | undefined => {
          const raw = side?.token0 !== undefined ? side : { token0: side }
          const tokens = [
            normalizeUnderlying((raw?.token0 ?? '').toString().toLowerCase()),
          ]
          // `isSmart` — never a zero-address test on token1 — decides arity:
          // Fluid's native-ETH sentinel normalizes to the zero address, which
          // is also what an absent token1 decodes to.
          if (isSmart)
            tokens.push(
              normalizeUnderlying((raw?.token1 ?? '').toString().toLowerCase()),
            )
          if (!tokens[0]) return undefined

          // A smart side with no DEX state cannot be converted from shares to
          // tokens. Bail out entirely rather than fall through to a zero
          // per-share, which would silently report the position as empty.
          let perShare: [bigint, bigint] | undefined
          if (isSmart) {
            const state = dexState?.[String(sideContract ?? '').toLowerCase()]
            if (!state) return undefined
            perShare = perShareOf(state)
          }

          // Public data is the authority on which legs exist: it already
          // resolved decimals and dropped any vault it could not size. A leg
          // with no market row means the vault is not being served, so the
          // position must not be invented here either.
          const legs: PositionLeg[] = []
          for (let i = 0; i < tokens.length; i++) {
            const token = tokens[i]
            const marketUid = createMarketUid(chainId, lenderKey, token)
            const m = metaMap[marketUid]
            if (!m) return undefined
            legs.push({
              token,
              marketUid,
              decimals: m.asset?.decimals ?? 18,
              displayPrice: getDisplayPrice(m),
              oraclePrice: getOraclePrice(m),
              priceHist: m.price?.priceUsd24h ?? getDisplayPrice(m),
              // Simple side: the raw amount IS the token amount.
              // Smart side: it is a 1e18 LP share count, converted with the
              // DEX's per-share amounts (each already in ITS token's decimals).
              perShare: perShare?.[i],
            })
          }
          return legs
        }

        const colLegs = sideLegs(
          constants.supplyToken,
          isSmartCol,
          constants.supply,
          (s) => [s.token0PerSupplyShare, s.token1PerSupplyShare],
        )
        const debtLegs = sideLegs(
          constants.borrowToken,
          isSmartDebt,
          constants.borrow,
          (s) => [s.token0PerBorrowShare, s.token1PerBorrowShare],
        )
        if (!colLegs || !debtLegs) continue

        // Build the per-NFT sub-account structure that
        // `createMultiAccountTypeUserState` expects:
        //   lendingPositions[nftId][marketUid] = { deposits, debt, ... }
        const lendingPositions: Record<string, Record<string, any>> = {}
        const modes: Record<string, any> = {}
        const histData: Record<
          string,
          { totalDeposits24h: number; totalDebt24h: number }
        > = {}

        for (const { pos } of nfts) {
          const nftId = pos.nftId.toString()
          const supplyRaw = toBigInt(pos.supply)
          const borrowRaw = toBigInt(pos.borrow) + toBigInt(pos.dustBorrow)

          if (supplyRaw === 0n && borrowRaw === 0n) continue

          // Fluid vaults use a single mode ('0')
          modes[nftId] = 0

          const posData: Record<string, any> = {}
          let deposits24h = 0
          let debt24h = 0

          /**
           * A token can be BOTH collateral and debt in the same position: on a
           * same-DEX T4 vault (wstETH+ETH against wstETH+ETH) the collateral
           * and debt legs are the very same addresses, so they must merge into
           * one entry rather than overwrite each other — exactly as the
           * public-data rows do.
           */
          const entryFor = (leg: PositionLeg) => {
            const existing = posData[leg.marketUid]
            if (existing) return existing
            const created = {
              marketUid: leg.marketUid,
              underlying: leg.token,
              deposits: '0',
              debt: '0',
              debtStable: '0',
              depositsUSD: 0,
              debtUSD: 0,
              debtStableUSD: 0,
              depositsUSDOracle: 0,
              debtUSDOracle: 0,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              collateralEnabled: false,
              claimableRewards: 0,
            }
            posData[leg.marketUid] = created
            return created
          }

          /** Raw amount on a leg: shares * perShare / 1e18, or the raw itself. */
          const legRaw = (leg: PositionLeg, raw: bigint) =>
            leg.perShare === undefined ? raw : (raw * leg.perShare) / 10n ** 18n

          for (const leg of colLegs) {
            const amountStr = parseRawAmount(
              legRaw(leg, supplyRaw).toString(),
              leg.decimals,
            )
            const amount = Number(amountStr)
            const e = entryFor(leg)
            e.deposits = amountStr
            e.depositsUSD = amount * leg.displayPrice
            e.depositsUSDOracle = amount * leg.oraclePrice
            e.collateralEnabled = supplyRaw > 0n
            deposits24h += amount * leg.priceHist
          }

          for (const leg of debtLegs) {
            const amountStr = parseRawAmount(
              legRaw(leg, borrowRaw).toString(),
              leg.decimals,
            )
            const amount = Number(amountStr)
            const e = entryFor(leg)
            e.debt = amountStr
            e.debtUSD = amount * leg.displayPrice
            e.debtUSDOracle = amount * leg.oraclePrice
            debt24h += amount * leg.priceHist
          }

          histData[nftId] = {
            totalDeposits24h: deposits24h,
            totalDebt24h: debt24h,
          }

          lendingPositions[nftId] = posData
        }

        if (Object.keys(lendingPositions).length === 0) continue

        const userData = createMultiAccountTypeUserState(
          {
            chainId,
            account,
            lendingPositions,
            modes,
          },
          metaMap,
          histData,
        )

        if (userData && userData.data.length > 0) {
          out[lenderKey] = userData
        }
      }

      return Object.keys(out).length > 0 ? out : undefined
    },
    FLUID_USER_CALL_COUNT,
  ]
}
